import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useChannel, useEvent, usePresenceChannel } from '@harelpls/use-pusher';
import { Modal, ModalBody, Button, Card, CardGroup, ModalFooter, ModalHeader, Row, Col } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import Video from 'twilio-video';
import clsx from 'clsx';
import styled from 'styled-components';

import { createRoom, createToken, createComposition } from 'api/actions/integrations';
import { clientContractAction } from 'api/actions/contracts';
import { fetcher } from 'api/client';
import { CONTROL_MODAL, useSign, useSignDispatch, useSignState, VIDEO_CHAT_MODAL, ZOOM_VIDEO_CHAT_MODAL } from 'context/signview';
import { Circle } from './SignView';
import { initialLetters } from 'utils';
import Bootbox from 'components/Modals/Bootbox';
import {toast} from 'components/Shared/Toast';

const Lobby = ({
    handleSubmit,
    connecting,
    hasConnectionFailed,
    isHost,
    onReconnect,
    members = {}
}) => {

    const { t } = useTranslation();

    const dispatch = useSignDispatch();

    const { contract } = useSignState();

    const handleClose = () => {
        dispatch({ type: CONTROL_MODAL, close: VIDEO_CHAT_MODAL })
    }

    console.log({members, t: contract.participants?.filter(p => p.testamentRole === 'witness')})

    return (
        <div>
            <div className="float-right" onClick={handleClose}>
                <Button color="default" size="sm" style={{ marginRight: '-35px', marginTop: '-40px' }}>
                    <i className="fas fa-times-circle fa-2x light-gray"></i>
                </Button>
            </div>
            <ModalBody className="text-center">

                <h4 className="mt-4"><b>Sala de espera abierta</b></h4>
                <h6 className="mt-4 text-muted">Testigos que estan en linea</h6>
                <div className="d-flex justify-content-center flex-wrap">
                    {contract.participants?.map((participant) => {
                        const isOnline = members[participant._id];
                        return (
                            <div className="p-1 text-center border-0" key={participant._id}>
                                <Circle
                                    text={initialLetters(participant.name, participant.last_name)}
                                    className={clsx('border p-3', isOnline ? 'glow-success bg-success text-white' : 'bg-white')}
                                />
                            </div>
                        )
                    })}
                </div>
                <div className="text-left d-flex justify-content-center align-items-center flex-column text-muted mx-auto mt-2">
                    <span className="text-center">A continuación dará inicio la sesión, considera lo siguiente:</span>
                    <ul>
                        <li> El testador procederá a dar su declaración de voluntad</li>
                        <li> La grabación deberá durar máximo 3 minutos</li>
                        <li> Todos los testigos y testador deberán estar presentes</li>
                    </ul>
                </div>
                {/* <small className="text-muted">La grabacion debe durar maximo 3 minutos</small> */}
            </ModalBody>
            <ModalFooter>
                {
                    // eslint-disable-next-line no-nested-ternary
                    hasConnectionFailed ? 
                        <Button
                            block
                            color="primary"
                            className="text-center"
                            onClick={onReconnect}
                            disabled={connecting}>
                            <div className="text-center">{connecting ? t('Conectando') : t('Conectar')}</div>
                        </Button>
                    : isHost ?
                        <Button
                            block
                            color="primary"
                            className="text-center"
                            onClick={handleSubmit}
                            disabled={connecting || !contract.participants?.filter(p => p.testamentRole === 'witness').every(p => members[p._id])}>
                            <div className="text-center">{connecting ? t('Conectando') : t('Empezar declaración')}</div>
                        </Button>
                    :
                        <Button
                            block
                            color="primary"
                            className="text-center"
                            disabled={true}>
                            {t('Esperando al anfitrión')}
                        </Button>
                }
            </ModalFooter>
        </div>
    );
};

const DeclarationText = styled.div`
    position: absolute;
    width: 50%;
    bottom: 100px;
    left: 25%;
    background: black;
    color: white;
    padding: 10px;
    border-radius: 10px;
    opacity: .8;
`

const VideoChat = ({ setRoom, room }) => {

    const [{ contract = {}, profile, jwt }, dispatch] = useSign();
    const [roomConnectionParams, setRoomConnectionParams] = useState({});
    const [hasConnectionFailed, setHasConnectionFailed] = useState(false);

    const { members } = usePresenceChannel(`presence-video-${contract._id}`);

    const { t } = useTranslation();

    const channel = useChannel(contract._id ? `private-${contract._id}` : null);

    const isHost = contract.signViewOptions?.currentParticipant?.isHost;

    useEvent(channel, 'chatInit', ({ uniqueName, email }) => {
        console.log({uniqueName, email })
        if (email !== profile.email) {
            mutateCreateToken({ room: { uniqueName }, email: profile.email })
        }
    });

    useEvent(channel, 'chatClose', () => {
        doCloseAndReload()
    });

    useEvent(channel, 'chatRestart', () => {
        handleLogout()
    });

    const doCloseAndReload = () => {
        handleLogout();
        queryClient.invalidateQueries('client-contract');
        dispatch({ type: CONTROL_MODAL, close: VIDEO_CHAT_MODAL });
        Bootbox.alert(t('Creando declaración, cuando termine serás notificado, ahora puedes proceder con el proceso de firma'));
    }

    const { mutate: mutateCreateToken } = useMutation((payload) => fetcher(createToken(payload)), {
        onSuccess: ({ jwt }, { room }) => {
            console.log({jwt, room})
            setRoomConnectionParams({jwt, room})
            handleConnectToRoom({ jwt, room })
        },
    });
    

    const queryClient = useQueryClient();

    const { mutate: mutateCreateRoom } = useMutation((payload) => fetcher(createRoom(payload)));

    const { mutate: mutateClientContract } = useMutation((payload) => fetcher(clientContractAction(contract._id, 'endRecording', payload), jwt));
    const { mutate: mutateRestartRecording } = useMutation((payload) => fetcher(clientContractAction(contract._id, 'restartRecording', payload), jwt));

    const { mutate: mutateCreateComposition } = useMutation((payload) => fetcher(createComposition(payload)));

    const [connecting, setConnecting] = useState(false);

    const handleSubmit = useCallback((e) => {
        if (!contract._id || !profile.email) return;
        e.preventDefault();
        setConnecting(true);
        mutateCreateRoom({ contractId: contract._id, email: profile.email }, {
            onSuccess: (room) => {
                console.log({room})
                mutateCreateToken({ room, email: profile.email })
            }
        })
    }, [contract._id, profile.email]);

    const handleLogout = useCallback(() => {
        setRoom((prevRoom) => {
            if (prevRoom) {
                prevRoom.localParticipant.tracks.forEach((trackPub) => {
                    trackPub.track.stop();
                });
                prevRoom.disconnect();
            }
            return null;
        });
    }, []);

    const checkWitnesses = (videoparticipants, minimumWitnesses) => {
        const identities = videoparticipants.map(v => v.identity);
        const witnesses = contract?.participants?.filter(p => p.testamentRole === 'witness');
        const total = witnesses?.reduce((total, witness) => {
            if (identities.includes(witness.email)) total = total + 1
            return total;
        }, 0)

        return total >= minimumWitnesses;
    }

    const handleEndDeclaration = (videoparticipants) => {
        const hasMinimumWitnesses = checkWitnesses(videoparticipants, 3)
        if (!hasMinimumWitnesses) return toast.error('Para concluir la declaración se debe de tener al menos 3 testigos presentes')

        handleLogout();
        mutateCreateComposition({ sid: room.sid, contractId: contract._id }, {
            onSuccess: () => {
                mutateClientContract({ sid: room.sid }, {
                    onSuccess: () => {
                        doCloseAndReload()
                    }
                })
            }
        })
    }
    
    const handleRestartDeclaration = () => {
        mutateRestartRecording({}, { 
            onSucccess: () => {
                handleLogout();
            }
        })
        
    }

    const handleConnectToRoom = ({jwt,  room }) => {
        Video.connect(jwt, {
            name: room.uniqueName,
            // audio: !!isHost,
            dominantSpeaker: !!isHost,
        })
            .then((room) => {
                setConnecting(false);
                setRoom(room);
            })
            .catch((err) => {
                setConnecting(false);
                setHasConnectionFailed(true);
                console.log({err})
                toast.error('Hubo un problema al tratar de unirse a la sesión')
            });
    }

    const handleReconnect = () => {
        setConnecting(true);
        handleConnectToRoom(roomConnectionParams);
    }

    // useEffect(() => {
    //     if (room) {
    //         const tidyUp = (event) => {
    //             if (event.persisted) {
    //                 return;
    //             }
    //             if (room) {
    //                 handleLogout();
    //             }
    //         };
    //         window.addEventListener('pagehide', tidyUp);
    //         window.addEventListener('beforeunload', tidyUp);
    //         return () => {
    //             window.removeEventListener('pagehide', tidyUp);
    //             window.removeEventListener('beforeunload', tidyUp);
    //         };
    //     }
    // }, [room, handleLogout]);

    return room ?
        <Room
            room={room}
            onLogout={handleLogout}
            isHost={isHost}
            onEndDeclaration={handleEndDeclaration}
            onRestartDeclaration={handleRestartDeclaration}
            members={members}
        />
        :
        <Lobby
            members={members}
            isHost={isHost}
            hasConnectionFailed={hasConnectionFailed}
            handleSubmit={handleSubmit}
            onReconnect={handleReconnect}
            connecting={connecting}
        />
};

const ParticipantNameContainer = styled.div`
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    background: black;
    color: white;
    padding: 10px;
    border-radius: 10px;
    opacity: .7;
`

const Participant = ({ className, participant, isHost, contractParticipant }) => {

    const [videoTracks, setVideoTracks] = useState([]);
    const [audioTracks, setAudioTracks] = useState([]);

    const videoRef = useRef();
    const audioRef = useRef();

    const trackpubsToTracks = (trackMap) =>
        Array.from(trackMap.values())
            .map((publication) => publication.track)
            .filter((track) => track !== null);

    useEffect(() => {
        setVideoTracks(trackpubsToTracks(participant.videoTracks));
        setAudioTracks(trackpubsToTracks(participant.audioTracks));

        const trackSubscribed = (track) => {
            if (track.kind === 'video') {
                setVideoTracks((videoTracks) => [...videoTracks, track]);
            } else if (track.kind === 'audio') {
                setAudioTracks((audioTracks) => [...audioTracks, track]);
            }
        };

        const trackUnsubscribed = (track) => {
            if (track.kind === 'video') {
                setVideoTracks((videoTracks) => videoTracks.filter((v) => v !== track));
            } else if (track.kind === 'audio') {
                setAudioTracks((audioTracks) => audioTracks.filter((a) => a !== track));
            }
        };

        participant.on('trackSubscribed', trackSubscribed);
        participant.on('trackUnsubscribed', trackUnsubscribed);

        return () => {
            setVideoTracks([]);
            setAudioTracks([]);
            participant.removeAllListeners();
        };
    }, [participant]);

    useEffect(() => {
        const videoTrack = videoTracks[0];
        if (videoTrack) {
            videoTrack.attach(videoRef.current);
            return () => {
                videoTrack.detach();
            };
        }
    }, [videoTracks]);

    useEffect(() => {
        const audioTrack = audioTracks[0];
        if (audioTrack) {
            audioTrack.attach(audioRef.current);
            return () => {
                audioTrack.detach();
            };
        }
    }, [audioTracks]);

    return (
        <div
            className={clsx(`video-wrapper p-2 position-relative`, className)}
        >
            <video
                width="100%"
                height="100%"
                autoPlay={true}
                ref={videoRef}
                className={clsx('video ', isHost && 'border-primary border')}
            />
            <ParticipantNameContainer className="position-absolute">{contractParticipant?.name} {contractParticipant?.last_name}</ParticipantNameContainer>
            <audio ref={audioRef} autoPlay={true} />
        </div>
    );
};

const minInSecs = 60;

const DeclarationButtonContainer = styled.div`
    width: 600px;
    display: flex;
    justify-content: space-between;
`

const Room = ({ room, onLogout, isHost, onEndDeclaration, onRestartDeclaration }) => {

    const { t } = useTranslation();

    const [videoparticipants, setVideoParticipants] = useState([room.localParticipant]);

    const { contract = {} } = useSignState();

    const [time, setTime] = useState({ elapsedTime: 3 * minInSecs });

    useEffect(() => {
        const interval = setInterval(() => setTime(({ elapsedTime }) => ({ elapsedTime: elapsedTime - 1 })), 1000);
        return () => {
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        const participantConnected = (participant) => {
            setVideoParticipants((prevParticipants) => [...prevParticipants, participant]);
        };

        const participantDisconnected = (participant) => {
            setVideoParticipants((prevParticipants) =>
                prevParticipants.filter((p) => p !== participant)
            );
        };
        

        room.on('participantConnected', participantConnected);
        room.on('participantDisconnected', participantDisconnected);
        room.participants.forEach(participantConnected);
        return () => {
            room.off('participantConnected', participantConnected);
            room.off('participantDisconnected', participantDisconnected);
            setVideoParticipants([])
        };
    }, [contract.participants, room]);

    const {testatorName, witnessesNames, heirsNames} = useMemo(() => {
        const testator = contract.participants.find(p => p.testamentRole === 'testator');
        const witnesses = contract.participants.filter(p => ['witness'].includes(p.testamentRole)).map(p => `${p.name} ${p.last_name || ''}`);
        const heirs = contract.participants.filter(p => ['heir', 'legatee'].includes(p.testamentRole)).map(p => `${p.name} ${p.last_name || ''}`);

        return { 
            testatorName: `${testator.name} ${testator.last_name || ''}`, 
            witnessesNames: witnesses?.join(', ') || '', 
            heirsNames: heirs?.join(", ") || '' 
        }
    }, [contract.participants])

    useEffect(() => {
        if (time.elapsedTime <= 0 && isHost) {
            toast.error('Se llegó al limite de tiempo para grabar la declaración')
            onRestartDeclaration()
        }
    }, [time.elapsedTime]);

    const handleEndDeclaration = () => {
        onEndDeclaration(videoparticipants)
    }

    if (!room) return null;

    return (
        <div className={'d-flex flex-column overflow-hidden h-100'} >
            <div className="overflow-hidden d-flex flex-column h-100 position-relative">
                <div className="overflow-auto h-100 bg-dark px-5 py-4 d-flex justify-content-center">
                    <div className={'row justify-content-center align-items-center h-100 p-0'} style={{width: '70%'}}>
                        {videoparticipants?.map((participant, index) => {
                            const contractParticipant = contract.participants.find(p => p.email === participant.identity)
                            return (
                                    <Participant key={participant.sid} className={"col-sm-6 col-md-4"} participant={participant} contractParticipant={contractParticipant} isHost={contractParticipant.isHost}></Participant>
                            )
                        }
                            
                        )}
                    </div>
                </div>
                {isHost && <DeclarationText>

                    Yo {testatorName}, ante la presencia de los testigos {witnessesNames}, con fundamento en el 
                    artículo 1565 del Testamento Privado y demás correlativos en los ordenamientos locales y como 
                    consecuencia de las circunstancias descritas en el presente documento, manifiesto mi voluntad 
                    a través del presente mensaje de datos y mi firma electrónica para dar mi testamento 
                    en favor de {heirsNames}

                </DeclarationText>}

                <div className="d-flex justify-content-between bg-white py-4 px-5 mx-3">
                    <div className="align-self-center d-flex align-items-center">
                        <h5 className="m-0 p-0"><b>Tiempo: {Math.max(time.elapsedTime, 0)} segundos</b></h5>
                    </div>
                    <DeclarationButtonContainer className="align-self-center">
                        <Button className="mx-2" color="secondary" onClick={onRestartDeclaration} hidden={!isHost}>Reiniciar declaración</Button>
                        <Button color="danger" onClick={handleEndDeclaration} hidden={!isHost}>Terminar declaración</Button>
                    </DeclarationButtonContainer>
                    <div className="align-self-center"><h5 className="m-0 p-0"><b>{videoparticipants.length} personas</b></h5></div>
                </div>
            </div>
        </div >
    );
};

function Ask({ setPermissions }) {
    const { t } = useTranslation();

    const handleGivePermissions = () => {
        navigator.mediaDevices.getUserMedia({ video: true, audio: true }).then(() => {
            setPermissions(true)
        })
    }

    return (
        <>
            <ModalBody>
                {t('Antes de comenzar es necesario que podamos accesar a tu cámara y micrófono. Por favor haz clic en el botón para dar acceso.')}
            </ModalBody>
            <ModalFooter>
                <Button block onClick={handleGivePermissions}>Dar acceso</Button>
            </ModalFooter>
        </>
    )
}

export default function TestamentRecord() {
    const { modal } = useSignState();
    const [room, setRoom] = useState(null);
    const [hasPermissions, setPermissions] = useState(false);
    return (
        <Modal isOpen={modal.includes(VIDEO_CHAT_MODAL)} centered size={room ? 'fs' : 'md'}
            className={clsx(room && "h-100")}
            contentClassName={clsx(room && "h-100")}
        >
            {hasPermissions ?
                <VideoChat room={room} setRoom={setRoom}></VideoChat> :
                <Ask setPermissions={setPermissions} />
            }
        </Modal>
    )
}

export function ZoomRecord() {
    const [{ modal, contract = {}, jwt }, dispatch] = useSign();
    const [room, setRoom] = useState(null);
    const [connecting, setPermissions] = useState(false);
    
    const { members } = usePresenceChannel(`presence-video-${contract._id}`);

    const { t } = useTranslation();

    const channel = useChannel(contract._id ? `private-${contract._id}` : null);

    const isHost = contract.signViewOptions?.currentParticipant?.isHost;

    const { mutate: mutateCreateMeeting } = useMutation((payload) => fetcher(clientContractAction(contract._id, 'createZoomMeeting', payload), jwt), {
        onSuccess: ({ data }) => {
            const currentParticipant = contract?.signViewOptions?.currentParticipant
            window.open(currentParticipant.isHost ? data.start_url : data.join_url);
            console.log({zoom: contract.zoom})
        }
    });

    const handleSubmit = () => {
        // mutateCreateMeeting()
        const currentParticipant = contract?.signViewOptions?.currentParticipant
            window.open(currentParticipant.isHost ? contract.zoom.meeting.start_url : contract.zoom.meeting.join_url);
            console.log({zoom: contract.zoom})
       
    }

    const handleClose = () => {
        dispatch({ type: CONTROL_MODAL, close: ZOOM_VIDEO_CHAT_MODAL })
    }

    return (
        <Modal isOpen={modal.includes(ZOOM_VIDEO_CHAT_MODAL)} centered size={room ? 'fs' : 'md'}
            className={clsx(room && "h-100")}
            contentClassName={clsx(room && "h-100")}
        >
            <div>
            <div className="float-right" onClick={handleClose}>
                <Button color="default" size="sm" style={{ marginRight: '-35px', marginTop: '-40px' }}>
                    <i className="fas fa-times-circle fa-2x light-gray"></i>
                </Button>
            </div>
            <ModalBody className="text-center">

                <h4 className="mt-4"><b>Sala de espera abierta</b></h4>
                <h6 className="mt-4 text-muted">Participantes que estan en linea</h6>
                <div className="d-flex justify-content-center flex-wrap">
                    {contract.participants?.map((participant) => {
                        const isOnline = members[participant._id];
                        return (
                            <div className="p-1 text-center border-0" key={participant._id}>
                                <Circle
                                    text={initialLetters(participant.name, participant.last_name)}
                                    className={clsx('border p-3', isOnline ? 'glow-success bg-success text-white' : 'bg-white')}
                                />
                            </div>
                        )
                    })}
                </div>
            </ModalBody>
            <ModalFooter>
                    {/* hasConnectionFailed ? 
                        <Button
                            block
                            color="primary"
                            className="text-center"
                            onClick={onReconnect}
                            disabled={connecting}>
                            <div className="text-center">{connecting ? t('Conectando') : t('Conectar')}</div>
                        </Button>
                    :  */}
                {
                    isHost ?
                        <Button
                            block
                            color="primary"
                            className="text-center"
                            onClick={handleSubmit}
                            disabled={connecting}>
                            <div className="text-center">{connecting ? t('Conectando') : t('Empezar declaración')}</div>
                        </Button>
                    :
                        <Button
                            block
                            color="primary"
                            className="text-center"
                            disabled={true}>
                            {t('Unirse a la llamada')}
                        </Button>
                }
            </ModalFooter>
        </div>
        </Modal>
    )
}